import type { DailyForecast } from '@elara/domain/models/schemas/weather-schemas';

import { capitalize } from 'lodash-es';

import { cn } from '@elara/client-core/lib/utils';
import { mapWeatherConditionToWeatherIcon } from '@elara/domain/weather';

import { WeatherIcon } from '#ui/icons/weather-icons.js';
import { IconSizes } from '#ui/MaterialIcon.js';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

interface WeatherForecastProps {
  forecast: DailyForecast[];
  units: 'metric' | 'imperial';
  className?: string;
}

export function WeatherForecast({ forecast, units, className }: WeatherForecastProps) {
  if (forecast.length === 0) return undefined;

  return (
    <div className={cn('flex items-center gap-4 overflow-x-auto', className)}>
      {forecast.map((day: DailyForecast) => {
        const description = day.weather?.at(0)?.description ?? '';
        const icon = mapWeatherConditionToWeatherIcon(description);
        const date = new Date(day.date);
        const dayLabel = date.toLocaleDateString(undefined, { weekday: 'short' });
        const monthDay = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const unitSymbol = units === 'imperial' ? '°F' : '°C';
        return (
          <div key={day.date} className="p-2 flex flex-col items-center gap-3">
            <div className="text-sm leading-tight text-nowrap">
              {dayLabel} {monthDay}
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <WeatherIcon icon={icon} size={IconSizes.md} />
                </TooltipTrigger>
                <TooltipContent>{description ? capitalize(description) : ''}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex items-baseline gap-1 text-sm text-nowrap">
              <span>
                {Math.round(day.temperature.min)}
                {unitSymbol}
              </span>
              <span className="text-muted-foreground">
                {Math.round(day.temperature.max)}
                {unitSymbol}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
